import { useQuery } from '@tanstack/react-query';
import { fetchAllProducts, identifyBundles } from '@/services/shopify';
import type { ShopifyProduct, ShopifyBundle } from '@/types/shopify';

export interface UseAllProductsResult {
  allProducts: ShopifyProduct[];
  bundles: ShopifyBundle[];
  individualProducts: ShopifyProduct[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Hook to fetch all products from Shopify and categorize them
export const useAllProducts = (): UseAllProductsResult => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['all-products'],
    queryFn: async () => {
      console.log('[SHOPIFY] Fetching all products...');
      const allProducts = await fetchAllProducts();
      console.log('[SHOPIFY] Fetched products:', allProducts.length);
      
      const bundles = identifyBundles(allProducts);
      console.log('[SHOPIFY] Identified bundles:', bundles.length, bundles.map(b => b.handle));
      
      // Filter out individual products (non-bundles)
      const bundleHandles = new Set(bundles.map(b => b.handle));
      const individualProducts = allProducts.filter(p => !bundleHandles.has(p.handle));
      
      return {
        allProducts,
        bundles,
        individualProducts
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    allProducts: data?.allProducts || [],
    bundles: data?.bundles || [],
    individualProducts: data?.individualProducts || [],
    isLoading,
    error: error as Error | null,
    refetch
  };
};

// Hook to get only bundles
export const useBundles = () => {
  const { bundles, isLoading, error, refetch } = useAllProducts();
  return { bundles, isLoading, error, refetch };
};